import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useStableAuth } from "@/hooks/useStableAuth";
// Regra de sigla única (ENTREGA-QTD-BASE): mora em src/lib/saleItemQuantity.ts
import { siglaDe } from "@/lib/saleItemQuantity";

// I.151 1b3-a: unidades do produto — base (products.measurement_unit_id) + alternativas
// (product_units). Escrita só por upsert_product_unit (porta única; sem DELETE: inativa-se).
// A tela não converte nada: quem converte é o banco (convert_to_base nas RPCs de compra).

export interface ProductBaseUnit {
  id: string;
  name: string;
  sigla: string;
}

export interface ProductUnit {
  id: string;
  measurement_unit_id: string;
  name: string;
  sigla: string;
  label: string | null;
  factor_to_base: number | null;
  unit_weight_kg: number | null;
  is_approximate: boolean;
  allow_purchase: boolean;
  allow_count: boolean;
  active: boolean;
}

export interface UpsertProductUnitInput {
  productId: string;
  measurementUnitId: string;
  factorToBase: number | null;
  isApproximate?: boolean;
  unitWeightKg?: number | null;
  allowPurchase?: boolean;
  allowCount?: boolean;
  label?: string | null;
  active?: boolean;
}


// I.151 1b3-a (decisão do Diretor): a unidade base do produto não muda pela ficha quando já há
// unidade alternativa ativa, saldo diferente de zero ou qualquer movimento de estoque — senão o
// estoque e os fatores passariam a significar outra coisa. (O ato 2 troca a base no banco.)
// I.151 1b3-b (decisão do Diretor): fail-closed — conferindo ou com falha na conferência, a base
// fica travada. Produto novo (sem id) não tem o que conferir: livre.
export interface ProductBaseUnitLock {
  locked: boolean;
  reason: string | null;
}

export function useProductBaseUnitLock(productId: string | null | undefined): ProductBaseUnitLock {
  const { companyId } = useStableAuth();
  const query = useProductBaseUnitLockQuery(productId, companyId);
  if (!productId) return { locked: false, reason: null };
  if (query.isError) {
    return { locked: true, reason: "não foi possível conferir a unidade base — feche e abra a ficha de novo" };
  }
  if (query.isPending) return { locked: true, reason: "conferindo a unidade base…" };
  return query.data ? { locked: true, reason: query.data } : { locked: false, reason: null };
}

function useProductBaseUnitLockQuery(productId: string | null | undefined, companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["product-base-unit-lock", companyId, productId],
    queryFn: async (): Promise<string | null> => {
      const [alts, prod, movs] = await Promise.all([
        supabase
          .from("product_units")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId as string)
          .eq("product_id", productId as string)
          .eq("active", true),
        supabase
          .from("products")
          .select("stock_quantity")
          .eq("company_id", companyId as string)
          .eq("id", productId as string)
          .maybeSingle(),
        supabase
          .from("stock_movements")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId as string)
          .eq("product_id", productId as string),
      ]);
      if (alts.error) throw alts.error;
      if (prod.error) throw prod.error;
      if (movs.error) throw movs.error;
      if ((alts.count ?? 0) > 0) return "o produto tem unidade alternativa ativa";
      if (Number(prod.data?.stock_quantity ?? 0) !== 0) return "o produto tem saldo em estoque";
      if ((movs.count ?? 0) > 0) return "o produto já tem movimento de estoque";
      return null;
    },
    enabled: !!companyId && !!productId,
    staleTime: 30 * 1000,
  });
}

export function useProductUnits(productId: string | null | undefined) {
  const { companyId } = useStableAuth();
  const queryClient = useQueryClient();
  const enabled = !!companyId && !!productId;

  const baseQuery = useQuery({
    queryKey: ["product-base-unit", companyId, productId],
    queryFn: async (): Promise<ProductBaseUnit | null> => {
      const { data, error } = await supabase
        .from("products")
        .select("measurement_unit_id, measurement_units!products_measurement_unit_id_fkey(id, name, sefaz_code)")
        .eq("id", productId as string)
        .eq("company_id", companyId as string)
        .maybeSingle();
      if (error) throw error;
      const mu = (data as any)?.measurement_units;
      if (!mu) return null;
      return { id: mu.id, name: mu.name, sigla: siglaDe(mu) };
    },
    enabled,
    staleTime: 60 * 1000,
  });

  const unitsQuery = useQuery({
    queryKey: ["product-units", companyId, productId],
    queryFn: async (): Promise<ProductUnit[]> => {
      const { data, error } = await supabase
        .from("product_units")
        .select("id, measurement_unit_id, label, factor_to_base, unit_weight_kg, is_approximate, allow_purchase, allow_count, active, measurement_units!product_units_measurement_unit_id_fkey(name, sefaz_code)")
        .eq("company_id", companyId as string)
        .eq("product_id", productId as string)
        .order("created_at");
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        measurement_unit_id: r.measurement_unit_id,
        name: r.measurement_units?.name ?? "",
        sigla: siglaDe(r.measurement_units),
        label: r.label,
        factor_to_base: r.factor_to_base,
        unit_weight_kg: r.unit_weight_kg,
        is_approximate: r.is_approximate,
        allow_purchase: r.allow_purchase,
        allow_count: r.allow_count,
        active: r.active,
      }));
    },
    enabled,
    staleTime: 60 * 1000,
  });

  const upsertMutation = useMutation({
    mutationFn: async (input: UpsertProductUnitInput) => {
      const { data, error } = await supabase.rpc("upsert_product_unit", {
        p_product_id: input.productId,
        p_measurement_unit_id: input.measurementUnitId,
        p_factor_to_base: input.isApproximate ? (null as unknown as number) : (input.factorToBase as number),
        p_is_approximate: input.isApproximate ?? false,
        p_unit_weight_kg: input.unitWeightKg ?? undefined,
        p_allow_purchase: input.allowPurchase ?? true,
        p_allow_count: input.allowCount ?? true,
        p_label: input.label ?? undefined,
        p_active: input.active ?? true,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, input) => {
      queryClient.invalidateQueries({ queryKey: ["product-units", companyId, input.productId] });
      queryClient.invalidateQueries({ queryKey: ["product-base-unit-lock", companyId, input.productId] });
      toast.success(input.active === false ? "Unidade inativada" : "Unidade salva");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao salvar unidade: ${error.message}`);
    },
  });

  return {
    base: baseQuery.data ?? null,
    units: unitsQuery.data ?? [],
    isLoading: baseQuery.isLoading || unitsQuery.isLoading,
    upsertUnit: upsertMutation.mutateAsync,
    isSaving: upsertMutation.isPending,
  };
}
